import { Ionicons } from '@expo/vector-icons';
import { router, useLocalSearchParams, type Href } from 'expo-router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, StyleSheet, Text, TextInput as RNTextInput, View } from 'react-native';

import { DateInput } from '../form/date-input';
import { Checkbox, DataTable, TextInput } from '../ui';

import { AccountDetailDrawer } from './account-detail-drawer';
import { AccountFlagBadges } from './account-flag-badges';
import {
	type CronAction, type CronPreviewRow, type PreviewPayload,
	accountLabel, actionNoun, dateFromYmd, defaultDuesPosting, defaultVisibleRows, duesNewBalanceCents, monthFromDate,
	repairSummaryText, resumeText, runChunkedCommit, splitChangedText, todayInEt, triggerCron, ymdFromDate,
} from './post-cron-shared';
import { type PostRunState, PostProgressScreen } from './post-progress-screen';

import { useAssociationDues, useBulkMarkInvoicesPrinted, useInvalidateAccounting } from '@/hooks/use-accounting';
import { useAssociation } from '@/hooks/use-data';
import { compareAccountCodes } from '@/utils/account-sort';
import { Alert } from '@/utils/alert';
import { cadencePeriod, type DuesCadence, duesPeriodLabel, snapToPeriodStart } from '@/utils/dues-period';
import { formatMoneyCents } from '@/utils/format-money';
import { openPrintRouteChecked } from '@/utils/print-navigation';
import { openReportPrintWindow, type PrintColumn } from '@/utils/report-print';

/** How long a date/period edit must sit still before it re-runs the (slow) dry-run preview. */
const PREVIEW_DEBOUNCE_MS = 400;

const PREVIEW_TABLE_STYLES = {
	headRow: { style: { backgroundColor: '#bf9000', borderBottomWidth: 3, borderBottomColor: '#7a5c00', borderBottomStyle: 'solid', minHeight: 44 } },
	headCells: { style: { color: '#ffffff', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 } },
	rows: { style: { backgroundColor: '#ffffff', minHeight: 48 }, stripedStyle: { backgroundColor: '#f5f5f5' } },
	cells: { style: { paddingTop: 8, paddingBottom: 8 } },
};

/**
 * Full-page Post Dues / Post Late Fees (per-association). Replaces the old
 * modal for the per-association scope so the preview can show richer detail and
 * a click-to-inspect drawer. The portfolio-wide "ALL" scope still uses the
 * modal (`post-cron-button.tsx`) since it has no per-association URL.
 */
export const PostCronPage: React.FC<{ action: CronAction; associationId: string; onSummaryChange?: (s: string) => void }> = ({ action, associationId, onSummaryChange }) => {
	const invalidate = useInvalidateAccounting();
	const { backTo } = useLocalSearchParams<{ backTo?: string }>();
	const noun = actionNoun(action);
	// For the print header (React Query dedupes with the wrapper's same call).
	const { data: association } = useAssociation(associationId);

	// Where to return after a successful post: the URL's backTo, else the
	// association dashboard. router.replace so the (now-stale) post page is
	// dropped from the back stack.
	const goBack = useCallback(() => {
		router.replace((backTo || `/associations/${associationId}?tab=accounting`) as Href);
	}, [backTo, associationId]);
	const todayEt = useMemo(todayInEt, []);
	const [postingDate, setPostingDate] = useState<string>(todayEt);
	// `forPeriod` is always the YYYY-MM the period STARTS on; the server maps it onto the
	// association's cadence key (annual `2027`, quarterly `2027-Q1`).
	const [forPeriod, setForPeriod] = useState<string>(monthFromDate(todayEt));
	// Dues: the association's cadence picks the default period and snaps the picker. The first
	// preview waits for it, so an annual association never previews a stray month.
	const duesQuery = useAssociationDues(action === 'post-dues' ? associationId : undefined);
	const cadence: DuesCadence = (action === 'post-dues' ? duesQuery.data?.cadence : undefined) ?? 'monthly';
	const [periodReady, setPeriodReady] = useState(action !== 'post-dues');
	const duesDay = duesQuery.data?.dayOfMonth;
	useEffect(() => {
		if (periodReady || !(duesQuery.isFetched || duesQuery.isError)) return;
		// Dues post in advance: both fields default to the NEXT dues date, not today.
		const next = defaultDuesPosting(cadence, duesDay);
		setPostingDate(next.postingDate);
		setForPeriod(next.forPeriod);
		setPeriodReady(true);
	}, [periodReady, duesQuery.isFetched, duesQuery.isError, cadence, duesDay]);
	// What the PM sees: the cadence's own key ("2027", "2027-Q1", "2026-10").
	const periodLabel = cadencePeriod(cadence, forPeriod) ?? forPeriod;
	// Late fees: the post date auto-tracks (FOR PERIOD start + grace days) until
	// the user pins it by editing the post date directly.
	const postDateTouched = useRef(false);
	// Optional override for the FIRST invoice number (blank = use the natural next
	// number). Seeds the inline, per-row Invoice # column below.
	const [invoiceOverride, setInvoiceOverride] = useState('');
	// Per-row invoice-number edits (accountId → number). Absent rows fall back to
	// the sequential number computed from the starting number.
	const [invNums, setInvNums] = useState<Record<string, string>>({});
	const [previewData, setPreviewData] = useState<PreviewPayload | null>(null);
	const [busy, setBusy] = useState(false);
	const [run, setRun] = useState<PostRunState | null>(null);
	const posting = run?.phase === 'posting';
	const bulkMarkPrinted = useBulkMarkInvoicesPrinted();
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [inspectRow, setInspectRow] = useState<CronPreviewRow | null>(null);
	// Late fees default to showing EVERY account with a positive balance (#304) —
	// PMs want the full delinquency picture, not just will-post rows. Dues keep the
	// will-post-only default. Either way the chip toggles between the two views.
	const [showAll, setShowAll] = useState(action === 'post-late-fees');
	// Live table sort, so Print can reproduce the on-screen order. Seeded with the
	// table's default sort (late fees: OVERDUE desc; dues: account order) and kept
	// in sync via DataTable's onSort. `null` = the base account-code order.
	const [sortState, setSortState] = useState<{ columnId: string; asc: boolean } | null>(
		action === 'post-late-fees' ? { columnId: 'overdue', asc: false } : null,
	);

	const sortedRows = useMemo<CronPreviewRow[]>(() => {
		const rows = [...(previewData?.details ?? [])];
		rows.sort((a, b) => compareAccountCodes(a.accountNumber ?? a.address ?? '', b.accountNumber ?? b.address ?? ''));
		return rows;
	}, [previewData]);

	const autoPosts = useCallback((r: CronPreviewRow) => r.willPost || r.skipReason === 'deselected', []);
	// Toggle-able rows: auto-post rows (uncheck to skip) plus rows a PM can force
	// on as a Manual Override — pause-flagged accounts, accounts sheltered by the
	// grace window, and accounts with nothing overdue on a late-fee GL code. All
	// three are gates the server itself lets an explicit selection override, so
	// this list has to match them or the override is unreachable from the UI.
	const canToggle = useCallback(
		(r: CronPreviewRow) => autoPosts(r) || r.skipReason === 'paused'
			|| r.skipReason === 'below_water_threshold' || r.skipReason === 'no_triggering_charge',
		[autoPosts],
	);
	const toggleableRows = useMemo(() => sortedRows.filter(canToggle), [sortedRows, canToggle]);

	// The in-flight preview. A dry run takes ~15 s on a real association, and every date edit
	// used to start another without cancelling the last — up to five overlapped, and whichever
	// answered LAST won, so the table could show the preview for a date no longer picked. Each
	// new preview aborts the one before; an aborted one never touches state.
	const previewRequest = useRef<AbortController | null>(null);
	const refreshPreview = useCallback(async (date: string, period: string) => {
		previewRequest.current?.abort();
		const request = new AbortController();
		previewRequest.current = request;
		setBusy(true);
		try {
			const preview = await triggerCron(
				action, { associationId, dryRun: true, postingDate: date, forPeriod: period }, 90_000, request.signal,
			);
			if (!request.signal.aborted) setPreviewData(preview);
		} catch (e) {
			if (!request.signal.aborted) Alert.alert('Preview failed', e instanceof Error ? e.message : 'Unknown error');
		} finally {
			if (previewRequest.current === request) {
				previewRequest.current = null;
				setBusy(false);
			}
		}
	}, [action, associationId]);
	useEffect(() => () => previewRequest.current?.abort(), []);

	// Initial load at once; a date/period edit waits for the typing to settle, so stepping
	// through a date runs one preview rather than one per keystroke. The edit itself drops the
	// old date's in-flight preview (it must not land during the wait) and marks the page busy,
	// so Post stays disabled until the preview for the new date is in. Dues wait for the
	// cadence-seeded period: the "initial" load is the first one AFTER that.
	const firstPreview = useRef(true);
	useEffect(() => {
		if (!periodReady) return undefined;
		if (firstPreview.current) {
			firstPreview.current = false;
			void refreshPreview(postingDate, forPeriod);
			return undefined;
		}
		previewRequest.current?.abort();
		previewRequest.current = null;
		setBusy(true);
		const timer = setTimeout(() => void refreshPreview(postingDate, forPeriod), PREVIEW_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [refreshPreview, postingDate, forPeriod, periodReady]);

	// Fresh preview → default-check exactly the auto-post rows.
	useEffect(() => {
		if (!previewData?.details) return;
		setSelectedIds(new Set(previewData.details.filter(autoPosts).map((r) => r.accountId)));
	}, [previewData, autoPosts]);

	// Late fees: auto-set the post date to FOR PERIOD start + grace days (e.g.
	// period 2026-06 + 10-day grace → 2026-06-11) once the preview reports the
	// rule's grace. Skipped once the user pins the post date manually.
	useEffect(() => {
		if (action !== 'post-late-fees' || postDateTouched.current) return;
		const grace = previewData?.graceDays;
		if (grace == null) return;
		const start = dateFromYmd(`${forPeriod}-01`);
		const desired = ymdFromDate(new Date(start.getTime() + grace * 24 * 60 * 60 * 1000));
		if (desired !== postingDate) setPostingDate(desired);
	}, [action, previewData, forPeriod, postingDate]);

	const selectedCount = selectedIds.size;

	// Rows shown in the table, or all of them when "Show all" is toggled on. Dues hide only
	// the never-charge rows (#333), so already-posted and paused accounts stay visible,
	// unchecked. Late fees keep the will-post filter, derived from selectedIds so a row
	// unchecked while filtered drops out of the view.
	const displayedRows = useMemo(
		() => showAll ? sortedRows : defaultVisibleRows(sortedRows, action, selectedIds),
		[showAll, sortedRows, action, selectedIds],
	);
	// Only rendered while filtered, when displayedRows IS the filtered set.
	const filteredLabel = action === 'post-dues'
		? `Chargeable only (${displayedRows.length})`
		: `Will-post only (${selectedCount})`;

	// Invoice numbering. The natural next number comes from the dry-run; the
	// "First invoice #" field can override it (floor). Each selected account gets
	// a sequential number (start + its position) that the user can edit per row
	// via the inline Invoice # column; per-row edits (invNums) win.
	const naturalNext = previewData?.nextInvoiceNumber;
	const effectiveStartNumber = naturalNext
		? (invoiceOverride.trim()
			? String(Math.max(Number(invoiceOverride) || 0, Number(naturalNext)))
			: naturalNext)
		: undefined;
	const projectedInvNums = useMemo(() => {
		const map: Record<string, string> = {};
		const base = Number(effectiveStartNumber);
		if (!Number.isFinite(base)) return map;
		// A resume onto its original invoice creates no invoice, so it takes no number.
		const ordered = sortedRows.filter((r) => selectedIds.has(r.accountId) && !r.resumeInvoiceNumber);
		ordered.forEach((r, i) => { map[r.accountId] = invNums[r.accountId] ?? String(base + i); });
		return map;
	}, [sortedRows, selectedIds, effectiveStartNumber, invNums]);

	// What this run will charge. `dueCents` is already the missing amount on a resume row.
	const selectedTotalCents = useMemo(
		() => sortedRows.reduce((sum, r) => sum + (selectedIds.has(r.accountId) ? r.dueCents : 0), 0),
		[sortedRows, selectedIds],
	);
	const selectionSummary = `${selectedCount} of ${toggleableRows.length} ${noun} selected · ${formatMoneyCents(selectedTotalCents)}`;

	useEffect(() => {
		onSummaryChange?.(sortedRows.length === 0
			? `No accounts for ${periodLabel}`
			: `${selectionSummary} · ${postingDate}`);
	}, [onSummaryChange, sortedRows.length, selectionSummary, postingDate, periodLabel]);

	const toggleAccount = useCallback((accountId: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(accountId)) next.delete(accountId); else next.add(accountId);
			return next;
		});
	}, []);

	const toggleAll = useCallback(() => {
		setSelectedIds((prev) => {
			if (prev.size >= toggleableRows.length && toggleableRows.length > 0) return new Set();
			return new Set(toggleableRows.map((r) => r.accountId));
		});
	}, [toggleableRows]);

	const headerStatus: 'checked' | 'unchecked' | 'indeterminate'
		= toggleableRows.length === 0 || selectedCount === 0 ? 'unchecked'
			: selectedCount >= toggleableRows.length ? 'checked' : 'indeterminate';

	const skipText = useCallback((r: CronPreviewRow): string => {
		switch (r.skipReason) {
			case 'already_posted': return `Already posted for ${periodLabel}`;
			case 'zero_due': return 'No dues set';
			case 'paused': return 'Payments paused (flag)';
			case 'cap_reached': return 'Max late fees reached';
			case 'fee_zero': return 'Computed fee is $0';
			case 'no_rules': return 'No late-fee rules configured';
			case 'credit_covers': return 'Account in credit (paid up)';
			case 'below_water_threshold': return (r.mostRecentWaterBillCents ?? 0) > 0
				? `Within most recent water bill (${formatMoneyCents(r.mostRecentWaterBillCents ?? 0)})`
				: 'No outstanding balance';
			case 'no_triggering_charge': return 'Nothing overdue on a late-fee GL code';
			case 'split_changed': return splitChangedText(r);
			default: return 'Will not post';
		}
	}, [periodLabel]);

	// The plain-text status shown on screen, reused for the print column.
	const statusText = useCallback((r: CronPreviewRow): string => {
		const checked = selectedIds.has(r.accountId);
		const auto = autoPosts(r);
		if (checked && auto) return r.resumesPartial ? resumeText(r) : 'Will post';
		if (checked) return 'Manual Override';
		if (auto) return 'Skipped (unchecked)';
		return skipText(r);
	}, [selectedIds, autoPosts, skipText]);

	const printAccount = useCallback((r: CronPreviewRow): string => {
		const label = accountLabel(r);
		return r.flags && r.flags.length > 0 ? `${label} [${r.flags.join(', ')}]` : label;
	}, []);

	// Print the currently-shown rows via the shared report print window (web only).
	// Mirrors the on-screen columns; the Water column is included only when the
	// association bills water, matching the table. A leading "Charge" column marks
	// the lines that will post with a clear ✗, and the rows are reproduced in the
	// table's CURRENT sort order (sortState), not the base account-code order.
	const handlePrint = useCallback(() => {
		const overdueBalance = (r: CronPreviewRow) => r.balanceAsOfPostDateCents ?? r.currentBalanceCents;
		const willCharge = (r: CronPreviewRow) => selectedIds.has(r.accountId);
		const feeForRow = (r: CronPreviewRow) => (willCharge(r) ? r.dueCents : null);
		// Marker column: a clear X on rows that WILL be charged this run, blank otherwise.
		const chargeCol: PrintColumn<CronPreviewRow> = { header: 'Charge', accessor: (r) => (willCharge(r) ? 'X' : ''), align: 'center', width: 60 };
		const lateColumns: PrintColumn<CronPreviewRow>[] = [
			chargeCol,
			{ header: 'Account', accessor: printAccount, align: 'left', width: 240 },
			{ header: 'Overdue', accessor: overdueBalance, format: 'money', align: 'right' },
			...(previewData?.waterBilled
				? [{ header: 'Water', accessor: (r: CronPreviewRow) => r.waterBalanceAsOfPostDateCents ?? 0, format: 'money' as const, align: 'right' as const }]
				: []),
			{ header: 'Fee', accessor: feeForRow, format: 'money', align: 'right' },
			{ header: 'New Balance', accessor: (r: CronPreviewRow) => overdueBalance(r) + (willCharge(r) ? r.dueCents : 0), format: 'money', align: 'right' },
			{ header: 'Status', accessor: statusText, align: 'left' },
		];
		const duesColumns: PrintColumn<CronPreviewRow>[] = [
			chargeCol,
			{ header: 'Account', accessor: printAccount, align: 'left', width: 240 },
			{ header: 'Current Balance', accessor: (r: CronPreviewRow) => r.currentBalanceCents, format: 'money', align: 'right' },
			{ header: 'Due', accessor: feeForRow, format: 'money', align: 'right' },
			{ header: 'New Balance', accessor: (r: CronPreviewRow) => duesNewBalanceCents(r, willCharge(r)), format: 'money', align: 'right' },
			{ header: 'Status', accessor: statusText, align: 'left' },
		];
		// Reproduce the table's current sort. Selectors mirror the on-screen columns
		// so a click-sorted table prints in the same order (RDT sorts numerically on
		// number selectors, else by string compare).
		const sortValue = (id: string, r: CronPreviewRow): number | string => {
			switch (id) {
				case 'overdue': return overdueBalance(r);
				case 'water': return r.waterBalanceAsOfPostDateCents ?? 0;
				case 'fee': return r.dueCents;
				case 'current': return r.currentBalanceCents;
				case 'due': return r.dueCents;
				case 'balance': return action === 'post-dues'
					? duesNewBalanceCents(r, willCharge(r))
					: overdueBalance(r) + (willCharge(r) ? r.dueCents : 0);
				case 'status': return r.willPost ? 'will-post' : (r.skipReason ?? 'skip');
				default: return r.accountNumber ?? ''; // mirrors the Account column selector
			}
		};
		const rows = [...displayedRows];
		if (sortState) {
			const { columnId, asc } = sortState;
			rows.sort((a, b) => {
				const av = sortValue(columnId, a);
				const bv = sortValue(columnId, b);
				const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
				return asc ? cmp : -cmp;
			});
		}
		openReportPrintWindow<CronPreviewRow>({
			title: action === 'post-late-fees' ? 'Post Late Fees' : 'Post Dues',
			associationName: association?.name,
			subtitle: `For period ${periodLabel} · Post date ${postingDate}`,
			summary: selectionSummary,
			columns: action === 'post-late-fees' ? lateColumns : duesColumns,
			rows,
		});
	}, [action, association, periodLabel, postingDate, selectionSummary, displayedRows, selectedIds, previewData, printAccount, statusText, sortState]);

	// Keep `sortState` in lockstep with the table header the user clicks, so Print
	// reproduces the on-screen order. RDT still sorts the view itself (client-side).
	const handleSort = useCallback((column: { id?: string | number }, sortDirection: 'asc' | 'desc') => {
		if (column?.id != null) setSortState({ columnId: String(column.id), asc: sortDirection === 'asc' });
	}, []);

	const columns = useMemo(() => {
		const selectCol = {
			id: 'select',
			name: <Checkbox status={headerStatus} onPress={toggleAll} disabled={toggleableRows.length === 0} />,
			width: '52px',
			cell: (r: CronPreviewRow) => (
				<Checkbox status={selectedIds.has(r.accountId) ? 'checked' : 'unchecked'} onPress={() => toggleAccount(r.accountId)} disabled={!canToggle(r)} />
			),
		};
		const accountCol = {
			id: 'account', name: 'Account', grow: 4, wrap: true, sortable: true,
			selector: (r: CronPreviewRow) => r.accountNumber ?? '',
			cell: (r: CronPreviewRow) => (
				<Pressable style={styles.accountCell} onPress={() => setInspectRow(r)}>
					<Text style={styles.accountLink}>{accountLabel(r)}</Text>
					{r.flags && r.flags.length > 0 ? (
						<View style={styles.accountFlags}><AccountFlagBadges flags={r.flags} size="compact" /></View>
					) : null}
				</Pressable>
			),
		};
		const statusCol = {
			id: 'status', name: 'Status', grow: 3, wrap: true, sortable: true,
			selector: (r: CronPreviewRow) => (r.willPost ? 'will-post' : (r.skipReason ?? 'skip')),
			cell: (r: CronPreviewRow) => {
				const checked = selectedIds.has(r.accountId);
				const auto = autoPosts(r);
				if (checked && auto) return <Text style={styles.willPost}>{r.resumesPartial ? resumeText(r) : 'Will post'}</Text>;
				if (checked) return <Text style={styles.override}>Manual Override</Text>;
				if (auto) return <Text style={styles.skipped}>Skipped (unchecked)</Text>;
				return <Text style={styles.skipped}>{skipText(r)}</Text>;
			},
		};
		// Inline, per-row invoice number — editable before posting. Only selected
		// (will-post) rows create an invoice, so only they show an input. A resume onto its
		// original invoice shows that invoice's number, read-only.
		const invoiceCol = {
			id: 'invoice', name: 'Invoice #', grow: 2,
			selector: (r: CronPreviewRow) => r.resumeInvoiceNumber ?? projectedInvNums[r.accountId] ?? '',
			cell: (r: CronPreviewRow) => !selectedIds.has(r.accountId) ? <Text style={styles.skipped}>—</Text>
				: r.resumeInvoiceNumber ? <Text testID={`resume-invoice-${r.accountId}`}>{r.resumeInvoiceNumber}</Text>
					: (
						<RNTextInput
							value={projectedInvNums[r.accountId] ?? ''}
							onChangeText={(t) => setInvNums((m) => ({ ...m, [r.accountId]: t.replace(/[^0-9]/g, '') }))}
							keyboardType="number-pad"
							style={styles.invInput}
							placeholder="—"
							placeholderTextColor="#bdbdbd"
						/>
					),
		};
		const newBalanceCol = {
			id: 'balance', name: 'New Balance', grow: 2, sortable: true,
			selector: (r: CronPreviewRow) => duesNewBalanceCents(r, selectedIds.has(r.accountId)),
			cell: (r: CronPreviewRow) => (
				<View>
					{selectedIds.has(r.accountId)
						? <Text>{formatMoneyCents(duesNewBalanceCents(r, true))}</Text>
						: <Text style={styles.skipped}>—</Text>}
					{r.balanceAsOfPostDateCents != null && (
						<Text style={styles.asOfSub}>as of {postingDate}: {formatMoneyCents(r.balanceAsOfPostDateCents)}</Text>
					)}
				</View>
			),
		};

		if (action === 'post-late-fees') {
			// OVERDUE = the account's full balance AS OF the post date (#304). The
			// per-GL breakdown still lives in the click-through drawer.
			const overdueBalance = (r: CronPreviewRow) => r.balanceAsOfPostDateCents ?? r.currentBalanceCents;
			const overdueCol = {
				id: 'overdue', name: 'Overdue', grow: 2, sortable: true,
				selector: overdueBalance,
				cell: (r: CronPreviewRow) => <Text style={styles.overdueTotal}>{formatMoneyCents(overdueBalance(r))}</Text>,
			};
			// Water — only shown for associations that bill water (#304). Reflects
			// the unpaid water balance as of the post date.
			const waterCol = {
				id: 'water', name: 'Water', grow: 2, sortable: true,
				selector: (r: CronPreviewRow) => r.waterBalanceAsOfPostDateCents ?? 0,
				cell: (r: CronPreviewRow) => (r.waterBalanceAsOfPostDateCents ?? 0) > 0
					? <Text>{formatMoneyCents(r.waterBalanceAsOfPostDateCents!)}</Text>
					: <Text style={styles.skipped}>—</Text>,
			};
			const feeCol = {
				id: 'fee', name: 'Fee', grow: 2, sortable: true,
				selector: (r: CronPreviewRow) => r.dueCents,
				cell: (r: CronPreviewRow) => !selectedIds.has(r.accountId)
					? <Text style={styles.skipped}>—</Text>
					: (
						<View>
							<Text>{formatMoneyCents(r.feeCents ?? r.dueCents)}</Text>
							{(r.legalAdminCents ?? 0) > 0 ? (
								<Text style={styles.legal}>+ {formatMoneyCents(r.legalAdminCents!)} legal</Text>
							) : null}
						</View>
					),
			};
			// New Balance = the as-of-post-date balance plus the fee this run adds.
			// This is the issue's primary figure; the table default-sorts by OVERDUE.
			const lateNewBalanceCol = {
				id: 'balance', name: 'New Balance', grow: 2, sortable: true,
				selector: (r: CronPreviewRow) => overdueBalance(r) + (selectedIds.has(r.accountId) ? r.dueCents : 0),
				cell: (r: CronPreviewRow) => (
					<Text style={selectedIds.has(r.accountId) ? undefined : styles.skipped}>
						{formatMoneyCents(overdueBalance(r) + (selectedIds.has(r.accountId) ? r.dueCents : 0))}
					</Text>
				),
			};
			return [
				selectCol, accountCol, overdueCol,
				...(previewData?.waterBilled ? [waterCol] : []),
				feeCol, lateNewBalanceCol, invoiceCol, statusCol,
			];
		}
		// Dues columns.
		return [
			selectCol, accountCol,
			{
				id: 'current', name: 'Current Balance', grow: 2, sortable: true,
				selector: (r: CronPreviewRow) => r.currentBalanceCents,
				cell: (r: CronPreviewRow) => formatMoneyCents(r.currentBalanceCents),
			},
			{
				id: 'due', name: 'Due', grow: 2, sortable: true,
				selector: (r: CronPreviewRow) => r.dueCents,
				cell: (r: CronPreviewRow) => selectedIds.has(r.accountId)
					? <Text>{formatMoneyCents(r.dueCents)}</Text>
					: <Text style={styles.skipped}>—</Text>,
			},
			newBalanceCol, invoiceCol, statusCol,
		];
	}, [action, headerStatus, toggleAll, toggleableRows.length, selectedIds, canToggle, autoPosts, skipText, toggleAccount, postingDate, previewData?.waterBilled, projectedInvNums]);

	const doPost = async () => {
		const ids = [...selectedIds];
		if (ids.length === 0) return;
		setRun({ phase: 'posting', done: 0, total: ids.length });
		try {
			const { posted, skipped, repaired, unfinished, invoiceIds } = await runChunkedCommit({
				action, associationId, postingDate, forPeriod,
				// Every dues invoice lands in the print queue; Print now / Print later is chosen on
				// the finished screen, so nothing about printing is decided before the post.
				printMode: action === 'post-dues' ? 'queue' : undefined,
				startInvoiceNumber: invoiceOverride.trim() || undefined,
				// Per-row numbers (computed sequence + any edits) drive the actual
				// assignment; startInvoiceNumber stays as a floor fallback.
				invoiceNumbers: projectedInvNums,
				selectedAccountIds: ids,
				onProgress: (done, total) => setRun({ phase: 'posting', done, total }),
			});
			await invalidate();
			setRun({
				phase: 'done',
				invoiceIds: action === 'post-dues' ? invoiceIds : [],
				message: `Posted ${posted} ${noun}.${skipped > 0 ? ` Skipped ${skipped}.` : ''}`
					+ repairSummaryText(repaired, unfinished)
					+ ' This run is recorded as a batch — open Admin → Batches to undo it.',
			});
		} catch (e) {
			setRun({
				phase: 'failed',
				message: (e instanceof Error ? e.message : 'Unknown error')
					+ ' Some may have already posted — check Admin → Batches before retrying.',
			});
		}
	};

	// The print click comes AFTER the post, so it is a fresh gesture and its tab opens normally.
	// Marked printed at once, like "Print queued" on the Invoices page, so they leave the queue.
	const printNow = (invoiceIds: string[]) => {
		const opened = openPrintRouteChecked(`/print/invoices/batch?ids=${invoiceIds.join(',')}`, (u) => router.push(u as Href));
		// Blocked: nothing printed, so nothing is marked and the screen stays for another try.
		if (opened === 'blocked') {
			setRun((r) => (r?.phase === 'done' ? { ...r, printBlocked: true } : r));
			return;
		}
		// In-app (off-web) lands on the print layout's "available on the web app" screen — nothing printed.
		if (opened === 'tab') bulkMarkPrinted.mutate(invoiceIds);
		setRun(null);
		// Off-web the batch was pushed onto THIS stack — goBack() would replace it.
		if (opened === 'tab') goBack();
	};
	const printLater = () => { setRun(null); goBack(); };
	const closeRun = () => {
		const failed = run?.phase === 'failed';
		setRun(null);
		// A failed run stays on the page, re-previewed, so the PM sees what did post.
		if (failed) void refreshPreview(postingDate, forPeriod);
		else goBack();
	};

	// A period whose every row reads "already posted" has nothing to select, so a posting that
	// died after its last insert could not be finished from here (#333). This runs the server's
	// repair alone: an empty selection with repairOnly, which inserts no charge.
	const unfinishedCount = action === 'post-dues' ? (previewData?.unfinishedCount ?? 0) : 0;
	const [finishing, setFinishing] = useState(false);
	const finishInterrupted = async () => {
		setFinishing(true);
		try {
			const result = await triggerCron(action, {
				associationId, postingDate, forPeriod, selectedAccountIds: [], repairOnly: true,
			}, 90_000);
			await invalidate();
			Alert.alert('Done', (repairSummaryText(result.repaired ?? 0, result.unfinished ?? []).trim()
				|| 'Nothing was left to finish.'));
			await refreshPreview(postingDate, forPeriod);
		} catch (e) {
			Alert.alert('Failed', e instanceof Error ? e.message : 'Unknown error');
		} finally {
			setFinishing(false);
		}
	};

	const confirmPost = () => {
		if (selectedCount === 0) return;
		Alert.alert(`Post ${selectedCount} ${noun}?`, `This posts ${formatMoneyCents(selectedTotalCents)} to ${selectedCount} account${selectedCount === 1 ? '' : 's'} for ${periodLabel}.`, [
			{ text: 'Cancel', style: 'cancel' },
			{ text: `Post ${selectedCount}`, onPress: () => void doPost() },
		]);
	};

	return (
		<View style={styles.wrap}>
			<View style={styles.controls}>
				<View style={styles.controlCol}>
					<DateInput label="Post date" value={dateFromYmd(postingDate)} onChange={(d) => {
						// Manual edit pins the post date — stop auto-deriving it from the period.
						postDateTouched.current = true;
						const next = ymdFromDate(d); setPostingDate(next);
						// Late fees' period follows the post date. Dues are billed in advance, so moving
						// the post date must not drag the period back off the next-period default.
						if (action === 'post-late-fees') setForPeriod(monthFromDate(next));
					}} />
					<Text style={styles.invHint} numberOfLines={1}>
						{action === 'post-dues' ? 'Defaults to the period start plus grace.' : 'Late fees post for this month.'}
					</Text>
				</View>
				<View style={styles.controlCol}>
					<DateInput label={cadence === 'monthly' ? 'For period' : `For period (${cadence})`} value={dateFromYmd(`${forPeriod}-01`)} onChange={(d) => {
						// Changing the period re-derives the post date (period start + grace).
						postDateTouched.current = false;
						setForPeriod(snapToPeriodStart(cadence, monthFromDate(ymdFromDate(d))));
					}} />
					{/* Every column carries a hint line so the input boxes share one baseline. */}
					<Text style={styles.invHint} numberOfLines={1} testID={action === 'post-dues' ? 'dues-period-label' : undefined}>
						{action === 'post-dues' ? duesPeriodLabel(cadence, forPeriod) : ' '}
					</Text>
				</View>
				<View style={styles.controlCol}>
					<TextInput
						mode="outlined"
						label="First invoice #"
						value={invoiceOverride}
						onChangeText={(t) => setInvoiceOverride(t.replace(/[^0-9]/g, ''))}
						placeholder={naturalNext ?? ''}
						placeholderTextColor="#bdbdbd"
						keyboardType="number-pad"
					/>
					<Text style={styles.invHint} numberOfLines={1}>Auto-fills the column — edit any row.</Text>
				</View>
				{sortedRows.length > 0 && (
					<View style={styles.controlActions}>
						<Pressable
							onPress={() => setShowAll((v) => !v)}
							style={[styles.willPostChip, !showAll && styles.chipActive]}
							testID="filter-will-post"
						>
							<Text style={[styles.chipText, !showAll && styles.chipTextActive]}>
								{showAll ? `Showing all (${sortedRows.length})` : filteredLabel}
							</Text>
						</Pressable>
						{Platform.OS === 'web' && (
							<Pressable onPress={handlePrint} style={styles.printChip} testID="print-report" accessibilityLabel="Print this report">
								<Ionicons name="print-outline" size={16} color="#37474f" />
								<Text style={styles.printChipText}>Print</Text>
							</Pressable>
						)}
					</View>
				)}
			</View>

			{unfinishedCount > 0 && (
				<View style={styles.unfinishedBanner} testID="unfinished-banner">
					<Ionicons name="warning-outline" size={18} color="#e65100" />
					<Text style={styles.unfinishedText}>
						{unfinishedCount} earlier posting{unfinishedCount === 1 ? '' : 's'} for {periodLabel}
						{unfinishedCount === 1 ? ' was' : ' were'} interrupted.
					</Text>
					<Pressable
						onPress={() => { void finishInterrupted(); }}
						disabled={finishing || posting}
						style={[styles.unfinishedButton, (finishing || posting) && { opacity: 0.5 }]}
						testID="finish-interrupted"
						accessibilityLabel="Finish interrupted postings"
					>
						<Text style={styles.unfinishedButtonText}>{finishing ? 'Finishing…' : 'Finish them'}</Text>
					</Pressable>
				</View>
			)}

			{busy && !previewData ? (
				<ActivityIndicator style={{ marginTop: 32 }} />
			) : sortedRows.length === 0 ? (
				<Text style={styles.empty}>No accounts found for {periodLabel} — change the date or period above.</Text>
			) : displayedRows.length === 0 ? (
				<Text style={styles.empty}>
					{action === 'post-dues'
						? `No account has dues to charge — tap “Chargeable only” to show all ${sortedRows.length}.`
						: `No accounts will post — tap “Will-post only” to show all ${sortedRows.length}.`}
				</Text>
			) : (
				<DataTable
					columns={columns}
					data={displayedRows}
					striped
					highlightOnHover
					pointerOnHover
					customStyles={PREVIEW_TABLE_STYLES}
					fixedHeader
					fixedHeaderScrollHeight="60vh"
					onSort={handleSort}
					{...(action === 'post-late-fees' ? { defaultSortFieldId: 'overdue', defaultSortAsc: false } : {})}
					conditionalRowStyles={[{ when: (r: CronPreviewRow) => r.accountId === inspectRow?.accountId, style: { backgroundColor: '#e3f2fd' } }]}
				/>
			)}

			<View style={styles.footer}>
				<View style={{ flex: 1 }} />
				<Pressable
					style={[styles.postBtn, (posting || busy || selectedCount === 0) && styles.postBtnDisabled]}
					disabled={posting || busy || selectedCount === 0}
					onPress={confirmPost}
				>
					<Text style={styles.postLabel}>{`Post ${selectedCount} ${noun}`}</Text>
				</Pressable>
			</View>

			<AccountDetailDrawer row={inspectRow} onClose={() => setInspectRow(null)} />
			<PostProgressScreen
				run={run}
				noun={noun}
				offersPrint={action === 'post-dues'}
				onPrintNow={printNow}
				onPrintLater={printLater}
				onClose={closeRun}
			/>
		</View>
	);
};

const HINT_GAP = 4;
const HINT_LINE_HEIGHT = 15;

const styles = StyleSheet.create({
	wrap: { flex: 1 },
	// Top-aligned, and every column ends in one fixed-height hint line, so the input boxes share a
	// baseline whether or not a field has helper text.
	controls: { flexDirection: 'row', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 12 },
	controlCol: { minWidth: 150 },
	invHint: { fontSize: 11, lineHeight: HINT_LINE_HEIGHT, height: HINT_LINE_HEIGHT, color: '#546e7a', marginTop: HINT_GAP },
	// The chips' bottoms line up with the input boxes: below each box sits DateInput's 12px margin
	// plus the hint line.
	controlActions: {
		flexDirection: 'row', gap: 8, marginLeft: 'auto', alignSelf: 'flex-end',
		marginBottom: 12 + HINT_GAP + HINT_LINE_HEIGHT,
	},
	invInput: {
		borderWidth: 1, borderColor: '#cfd8dc', borderRadius: 6,
		paddingVertical: 6, paddingHorizontal: 8, fontSize: 14,
		backgroundColor: '#fff', minWidth: 90, fontVariant: ['tabular-nums'],
	},
	// Match the DateInput box: 56px tall, 8px radius.
	willPostChip: {
		height: 56, justifyContent: 'center',
		paddingHorizontal: 16, borderRadius: 8, backgroundColor: '#e0e0e0',
	},
	printChip: {
		height: 56, flexDirection: 'row', alignItems: 'center', gap: 6,
		paddingHorizontal: 16, borderRadius: 8, backgroundColor: '#e0e0e0',
	},
	printChipText: { fontSize: 12, color: '#37474f', fontWeight: '600' },
	chipActive: { backgroundColor: '#1976d2' },
	chipText: { fontSize: 12, color: '#37474f', fontWeight: '600' },
	chipTextActive: { color: '#fff' },
	empty: { color: '#888', fontStyle: 'italic', textAlign: 'center', padding: 32 },
	unfinishedBanner: {
		flexDirection: 'row', alignItems: 'center', gap: 8, padding: 10, marginBottom: 12, borderRadius: 8,
		backgroundColor: '#fff3e0', borderWidth: 1, borderColor: '#ffcc80',
	},
	unfinishedText: { flex: 1, fontSize: 13, color: '#4e342e' },
	unfinishedButton: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 6, backgroundColor: '#e65100' },
	unfinishedButtonText: { fontSize: 12, color: '#fff', fontWeight: '700' },
	asOfSub: { fontSize: 10, color: '#90a4ae', marginTop: 1 },
	accountCell: { minWidth: 0, flexShrink: 1, paddingVertical: 4, gap: 3, width: '100%' },
	accountLink: { fontSize: 13, color: '#1565c0', fontWeight: '600' },
	accountFlags: { flexDirection: 'row', flexWrap: 'wrap', gap: 4 },
	overdueTotal: { fontSize: 14, fontWeight: '700', color: '#1a1a1a' },
	legal: { fontSize: 11, color: '#e65100', fontWeight: '600' },
	skipped: { color: '#888', fontStyle: 'italic' },
	willPost: { color: '#2e7d32', fontWeight: '600' },
	override: { color: '#e65100', fontWeight: '700' },
	footer: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 14, marginTop: 8, borderTopWidth: 1, borderTopColor: '#eceff1' },
	postBtn: { backgroundColor: '#007AFF', paddingHorizontal: 22, paddingVertical: 12, borderRadius: 6 },
	postBtnDisabled: { opacity: 0.5 },
	postLabel: { color: '#fff', fontWeight: '700', fontSize: 14 },
});
